import { useMemo } from 'react';

import { createTheme } from '@mui/material';

import colors from './colors';
import { useTheme } from './themeProvider';

import 'assets/fonts/aeonik.pro-bold-web/css/stylesheet-family-support.css';
import 'assets/fonts/aeonik.pro-regular-web/css/stylesheet-family-support.css';

import 'assets/fonts/space-grotesk/css/stylesheet-family-support.css';

import 'assets/fonts/clash_grotesk/css/stylesheet-family-support.css';

import 'assets/fonts/cantarell-regular/css/stylesheet-family-support.css';

import 'assets/fonts/dm-sans/css/stylesheet-family-support.css';

import 'assets/fonts/montserrat/css/stylesheet-family-support.css';

import 'assets/fonts/inter/css/stylesheet-family-support.css';

import 'assets/fonts/courier_prime/css/stylesheet-family-support.css';

declare module '@mui/material/styles' {
  interface Theme {
    colors: typeof colors;
    loader: {
      color: string;
    };
  }
  interface ThemeOptions {
    colors: typeof colors;
    loader?: {
      color: string;
    };
  }
}

export const useMuiTheme = () => {
  const { theme } = useTheme();

  // Memoize the theme creation to avoid unnecessary re-renders
  return useMemo(
    () =>
      createTheme({
        colors: colors,
        loader: {
          color: theme.loader,
        },
        palette: {
          primary: {
            main: theme.buttons.primary.color,
            contrastText: theme.buttons.primary.textColor,
          },
          secondary: {
            main: theme.buttons.secondary.color,
            contrastText: theme.buttons.primary.textColor,
          },
        },
        typography: {
          fontFamily: theme.fontFamily,
          h1: {
            fontSize: '72px',
          },
          h2: {
            fontSize: '36px',
          },
          h3: {
            fontSize: '24px',
          },
          h4: {
            fontSize: '18px',
          },
          body1: {
            fontSize: '18px',
          },
          caption: {
            fontSize: '14px',
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: '40px', // Common styles for all buttons
              },
              sizeSmall: {
                padding: '4px 14px', // Padding for small buttons
              },
              sizeMedium: {
                padding: '8px 24px', // Padding for medium (default) buttons
              },
              sizeLarge: {
                padding: '12px 28px', // Padding for large buttons
              },
              containedPrimary: {
                backgroundColor: theme.buttons.primary.color, // Primary button background
                color: theme.buttons.secondary.textColor, // Primary button text
                '&:hover': {
                  backgroundColor: theme.buttons.primary.hoverColor, // Primary button hover background
                  color: theme.buttons.primary.hoverText, // Primary button hover text
                },
                '&.Mui-disabled': {
                  backgroundColor: colors.grayLight2, // Custom disabled background for primary
                  color: theme.buttons.primary.hoverText, // Custom disabled text for primary
                },
              },
              containedSecondary: {
                backgroundColor: theme.buttons.secondary.color, // Secondary button background
                color: theme.buttons.secondary.textColor, // Secondary button text
                '&:hover': {
                  backgroundColor: theme.buttons.secondary.hoverColor, // Secondary button hover background
                  color: theme.buttons.secondary.hoverText, // Secondary button hover text
                },
                '&.Mui-disabled': {
                  backgroundColor: colors.grayLight2, // Custom disabled background for primary
                  color: theme.buttons.secondary.hoverText, // Custom disabled text for primary
                },
              },
            },
          },
        },
      }),
    [theme],
  );
};
